'use client'

import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CalendarIcon, CoinsIcon, RefreshCwIcon, TrendingUpIcon } from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { LoadingState } from '@/components/partials'
import { SortableHeader } from '@/components/partials'
import { useExchangeRates, useSyncExchangeRates } from '@/hooks'
import { ExchangeRatesFilterParams } from '@/types/exchange-rate'
import { SortEnum } from '@/enums'
import { formatDate, formatDateTime, formatNumber } from '@/lib/utils'

type PropsType = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ExchangeRatesSheet = ({ open, onOpenChange }: PropsType) => {
  const t = useTranslations()
  const locale = useLocale()
  const queryClient = useQueryClient()

  const [filters, setFilters] = useState<ExchangeRatesFilterParams>({
    page: 1,
    size: 20,
    sortBy: 'effectiveDate',
    sort: SortEnum.DESC
  })

  const { data, isLoading, error, refetch } = useExchangeRates(filters, { enabled: open })
  const { mutate: sync, isPending: isSyncing } = useSyncExchangeRates()

  const handleSort = (sortBy: string, sort: SortEnum) => {
    setFilters(prev => ({ ...prev, sortBy, sort, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (size: number) => {
    setFilters(prev => ({ ...prev, size, page: 1 }))
  }

  const handleSync = () => {
    sync(undefined, {
      onSuccess: async response => {
        await queryClient.invalidateQueries({ queryKey: ['exchange-rates'] })
        toast.success(t('exchange_rates_synced', { count: response.count }))
      },
      onError: (error: any) => {
        console.error('Error syncing exchange rates:', error)
        toast.error(error.response?.data?.message ?? t('exchange_rates_sync_failed'))
      }
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <TrendingUpIcon className="h-5 w-5" />
              {t('exchange_rates')}
            </SheetTitle>
            <Button size="sm" onClick={handleSync} disabled={isSyncing} className="mr-8">
              <RefreshCwIcon className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? t('syncing') : t('sync')}
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-6 py-4">
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={8}>
            {data && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-28">
                          <SortableHeader
                            sortBy="currencyCode"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CoinsIcon className="h-4 w-4" />
                            {t('currency_code')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-32 text-right">
                          <SortableHeader
                            sortBy="rate"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <TrendingUpIcon className="h-4 w-4" />
                            {t('rate')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-36">
                          <SortableHeader
                            sortBy="effectiveDate"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('effective_date')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-44">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.items.map((rate, index) => (
                          <TableRow key={rate.id} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/25'}>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">
                                {rate.currencyCode}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums">
                              {formatNumber(rate.rate, locale)}
                            </TableCell>
                            <TableCell className="text-center">{formatDate(rate.effectiveDate, locale)}</TableCell>
                            <TableCell className="text-muted-foreground text-center text-sm">
                              {formatDateTime(rate.createdAt, locale)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={data.page}
                  totalPages={data.pages}
                  totalItems={data.total}
                  pageSize={data.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default ExchangeRatesSheet
